/**
 * monitoring.js
 *
 * Central place for all production observability.
 * Called once from index.js after the app mounts.
 *
 * ── How to activate each service ────────────────────────────────────────────
 *
 * 1. Sentry (error tracking)
 *    npm install @sentry/react
 *    Set REACT_APP_SENTRY_DSN in .env.production
 *
 * 2. Google Analytics 4
 *    npm install react-ga4
 *    Set REACT_APP_GA_MEASUREMENT_ID in .env.production (format: G-XXXXXXXXXX)
 *
 * 3. Core Web Vitals
 *    Already uses the web-vitals package bundled with CRA.
 *    Metrics are sent to GA4 as custom events.
 */

// ── 1. Sentry ─────────────────────────────────────────────────────────────────
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn || process.env.NODE_ENV !== "production") return;

  // Uncomment after: npm install @sentry/react
  /*
  import("@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn,
      environment:        "production",
      release:            process.env.REACT_APP_VERSION || "1.0.0",
      tracesSampleRate:   0.1,   // capture 10 % of transactions for performance
      replaysOnErrorSampleRate: 1.0, // always capture replay on error
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
    });
  });
  */
};

// ── 2. Google Analytics 4 ─────────────────────────────────────────────────────
export const initAnalytics = () => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId || process.env.NODE_ENV !== "production") return;

  // Uncomment after: npm install react-ga4
  /*
  import("react-ga4").then(({ default: ReactGA }) => {
    ReactGA.initialize(measurementId);
    // Page view is tracked automatically via <RouteChangeTracker /> below.
  });
  */
};

// ── 3. Core Web Vitals → GA4 ──────────────────────────────────────────────────
export const reportWebVitals = (onPerfEntry) => {
  if (!onPerfEntry || typeof onPerfEntry !== "function") return;
  import("web-vitals").then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    getCLS(onPerfEntry);
    getFID(onPerfEntry);
    getFCP(onPerfEntry);
    getLCP(onPerfEntry);
    getTTFB(onPerfEntry);
  });
};

/**
 * Sends Core Web Vitals as GA4 custom events.
 * Pass this to reportWebVitals() in index.js.
 */
export const sendToAnalytics = ({ name, delta, value, id }) => {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", name, {
    event_category:  "Web Vitals",
    event_label:     id,
    value:           Math.round(name === "CLS" ? delta * 1000 : delta),
    non_interaction: true,
  });
};
